import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Link from '@mui/material/Link';
import ListItemButton from '@mui/material/ListItemButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from '@tyro/i18n';
import { ArrowRightIcon, PinIcon } from '@tyro/icons';
import { useState } from 'react';
import type { ReturnTypeFromUseDocumentationRelatedPages } from '../../api/pages';
import { PageKindIcon } from '../page-kind-icon';

interface ForThisPageCalloutProps {
  relatedPages: ReturnTypeFromUseDocumentationRelatedPages[];
  collapsedLimit: number;
  onSelectPage: (spaceId: number, pageSlug: string) => void;
}

export function ForThisPageCallout({
  relatedPages,
  collapsedLimit,
  onSelectPage,
}: ForThisPageCalloutProps) {
  const { t } = useTranslation(['documentation']);
  const [showAllRelated, setShowAllRelated] = useState(false);
  const headerLabel =
    `${t('documentation:reader.forThisPage')} · ${relatedPages[0].spaceName}`.toUpperCase();
  const canCollapse = relatedPages.length > collapsedLimit;
  const visiblePages =
    canCollapse && !showAllRelated
      ? relatedPages.slice(0, collapsedLimit)
      : relatedPages;

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: 'indigo.100',
        backgroundColor: 'indigo.50',
        boxShadow: 'none',
        p: 2,
      }}
    >
      <Stack gap={1.25}>
        <Stack direction="row" alignItems="center" gap={0.75}>
          <PinIcon sx={{ width: 14, height: 14, color: 'primary.main' }} />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              letterSpacing: 0.6,
            }}
          >
            {headerLabel}
          </Typography>
        </Stack>
        <Stack gap={0.25}>
          {visiblePages.map((page) => {
            return (
              <ListItemButton
                key={page.pageId}
                onClick={() => onSelectPage(page.spaceId, page.pageSlug)}
                sx={{
                  px: 1,
                  py: 0.75,
                  borderRadius: 1,
                  gap: 1.5,
                  '&:hover': { backgroundColor: 'indigo.100' },
                }}
              >
                <PageKindIcon entityType={page.entityType} withTooltip />
                <Typography
                  variant="body2"
                  sx={{ flex: 1, fontWeight: 500, color: 'text.primary' }}
                >
                  {page.title}
                </Typography>
                <ArrowRightIcon
                  sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }}
                />
              </ListItemButton>
            );
          })}
        </Stack>
        {canCollapse && (
          <Box sx={{ pl: 1 }}>
            <Link
              component="button"
              type="button"
              variant="caption"
              underline="hover"
              color="primary"
              onClick={() => setShowAllRelated((value) => !value)}
              sx={{ fontWeight: 600 }}
            >
              {showAllRelated
                ? t('documentation:reader.showLess')
                : t('documentation:reader.showMore', {
                    count: relatedPages.length - collapsedLimit,
                  })}
            </Link>
          </Box>
        )}
      </Stack>
    </Card>
  );
}
